import { useQuery } from "@tanstack/react-query";

import { backofficeClient } from "@/lib/api/backoffice-client";

/** Intent Hub authoring endpoints via the backoffice proxy. */
const INTENTS_PATH = "/proxy/intents/api/v1/intents";
/** The read the agent runtime makes: guidance already rendered to text. */
const RUNTIME_INTENTS_PATH = "/proxy/intents/api/v1/internal/intents";
/** Tools an intent can reference (the chips' ref vocabulary). */
const TOOLS_PATH = "/proxy/intents/api/v1/tools";

export type HubIntentStatus = "active" | "paused";
export type HubIntentType = "service" | "custom";

/** Mirrors intents-api IntentListItem. */
export interface HubIntentListItem {
  id: string;
  name: string;
  type: HubIntentType;
  status: HubIntentStatus;
  desk_id: string;
  category_id: string | null;
  reference_count: number;
  updated_at: string;
  updated_by: string | null;
}

export interface AudienceRules {
  target: "all" | "filter";
  departments: string[] | null;
}

/** The stored guidance envelope: TipTap JSON under `doc`. */
export interface GuidanceEnvelope {
  schema_version: number;
  doc: Record<string, unknown>;
}

/** Mirrors intents-api StoredIntentConfig. */
export interface HubIntentConfig {
  name: string;
  desk_id: string;
  category_id: string | null;
  description: string;
  when_to_use: string | null;
  examples: string[];
  when_not_to_use: string[];
  audience_rules: AudienceRules;
  guidance: GuidanceEnvelope | null;
}

export interface IntentReference {
  ref_type: string;
  ref: string;
}

/** Mirrors intents-api IntentResponse. */
export interface HubIntent {
  id: string;
  type: HubIntentType;
  status: HubIntentStatus;
  config: HubIntentConfig;
  updated_at: string;
  updated_by: string | null;
  row_version: number;
  references: IntentReference[];
  created_at: string;
  created_by: string | null;
}

/** Mirrors intents-api RuntimeIntent — what the agent actually reads. */
export interface RuntimeIntent {
  id: string;
  type: HubIntentType;
  status: HubIntentStatus;
  source_ref: string | null;
  desk_id: string;
  name: string;
  description: string | null;
  when_to_use: string | null;
  examples: string[];
  when_not_to_use: string[];
  clarification: string | null;
  wrapper: string | null;
  audience_rules: AudienceRules;
  instructions: string;
}

export function useHubIntents(tenantId: string | undefined) {
  return useQuery({
    queryKey: ["intent-hub", tenantId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<{
        intents: HubIntentListItem[];
      }>(INTENTS_PATH, { params: { tenant_id: tenantId } });
      return data.intents;
    },
    enabled: Boolean(tenantId),
  });
}

export function useHubIntent(tenantId: string, intentId: string) {
  return useQuery({
    queryKey: ["intent-hub", tenantId, intentId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<HubIntent>(
        `${INTENTS_PATH}/${intentId}`,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
  });
}

/**
 * The runtime read serves one status per call (active by default) and
 * leaves out intents whose document is missing or no longer parses.
 * Fetched once per tenant and status, joined by id for the open detail.
 */
export function useRuntimeIntents(
  tenantId: string,
  status: HubIntentStatus | undefined,
) {
  return useQuery({
    queryKey: ["intent-hub", tenantId, "runtime", status],
    queryFn: async () => {
      const { data } = await backofficeClient.get<{
        intents: RuntimeIntent[];
      }>(RUNTIME_INTENTS_PATH, { params: { tenant_id: tenantId, status } });
      return new Map(data.intents.map((i) => [i.id, i]));
    },
    enabled: Boolean(status),
  });
}

export interface ToolField {
  name: string;
  label: string;
  description: string | null;
  type: string;
  required: boolean;
  options: string[] | null;
}

/** Mirrors intents-api ToolResponse. */
export interface Tool {
  ref_type: string;
  ref: string;
  category: "read" | "action";
  label: string;
  description: string;
  hidden: boolean;
  agent_description: string | null;
  inputs: ToolField[];
  outputs: ToolField[];
}

/** The catalog is tenant-independent, but the proxy still needs a tenant. */
export function useTools(tenantId: string) {
  return useQuery({
    queryKey: ["intent-hub", tenantId, "tools"],
    queryFn: async () => {
      const { data } = await backofficeClient.get<{ tools: Tool[] }>(
        TOOLS_PATH,
        { params: { tenant_id: tenantId } },
      );
      return data.tools;
    },
  });
}

/** The 404 body when a ref is not a tool the Hub can offer. */
export interface ToolUnavailable {
  reason: string;
  message: string;
}

/**
 * Resolve one reference. Forms are not in the list, and a custom
 * workflow is not resolvable yet - both come back through this route.
 */
export function useTool(tenantId: string, refType: string, ref: string) {
  return useQuery({
    queryKey: ["intent-hub", tenantId, "tools", refType, ref],
    queryFn: async () => {
      const { data } = await backofficeClient.get<Tool>(
        `${TOOLS_PATH}/${refType}/${encodeURIComponent(ref)}`,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
    retry: false,
  });
}
